// src/components/BillPauseCard.tsx
//
// ★★★ WAVE 10 — THE THREE CONTROLS ANNEX A 37/38 ASKED FOR: pause a bill for
// information, put it back, and refer it to a colleague.
//
// ══ ★★★ WHY THIS IS ITS OWN CARD AND NOT THREE MORE BUTTONS IN `DecisionCard` ══
//
// `DecisionCard` answers one question — *may this officer decide this bill, and
// if not, why* — and it already carries eleven props to do it. These three are a
// different question: *what else can be done about this bill short of deciding
// it*. Folding them in would have meant three more comment fields, a colleague
// picker and three more submit handlers threaded through a component whose whole
// value is that one refusal sentence per action is impossible to forget.
//
// ⚠ And the distinction is the one `billAuthority` had to grow a second flag for:
// `hasAnyAction` (a DECISION is available) drives the approval queue, while
// `hasAnyControl` (any button at all) drives what renders. A referral is not
// progress on a bill; it is asking somebody else. Two cards, two questions.
//
// ══ ★★★ THE COMMENT IS MANDATORY ON ALL THREE, AND THE FORM SAYS WHAT FOR ══
//
// `bill_transition_comment_required` (060) refuses a `REQUEST_INFO`, `RESUME` or
// `REFER` row with no comment, and the reason is not bureaucratic: a pause with
// no statement of what is needed is the send-back trap itself — the HSP is told
// to supply something and not told what, and no later reader can close it either.
//
// ⚠ So each field's label asks for the SENTENCE rather than announcing that a
// field is required. An officer told "comment is required" types a full stop; the
// point of the mandatory comment is the sentence the next reader needs.
//
// ══ ★★★ THE COLLEAGUE IS PICKED FROM A LIST. NEVER TYPED. ══
//
// There is no text input for a colleague's name anywhere in this file, and there
// must never be one. Two colleagues can share a display name; resolving a typed
// name means `find(name)` returning whichever id sorts first, which would file
// the referral against the WRONG PERSON, render the RIGHT name, and be invisible
// at every layer a human checks — permanently, because the ledger refuses UPDATE
// and DELETE.
//
// ⚠ **AND THE LIST IS THE SERVER'S, not this component's filter of a roster.**
// `GET /api/bills/:reference/referral-candidates` returns exactly the colleagues
// `POST …/refer` will accept, computed by the same `authorise()` call, so the
// picker cannot offer a choice the write refuses. A referral to somebody who
// cannot read the bill would land in a ledger nobody reads and nobody would ever
// be told — which is worse than a refusal, because there is no refusal.
import { plainProse } from '../lib/plain-words'
import { useState } from 'react'
import { SgdsButton, SgdsSelect, SgdsSelectOption, SgdsTextarea } from '@govtechsg/sgds-web-component/react'
import { api } from '../lib/api'
import { targetValue } from '../lib/sgds-dom'
import { billStatusLabel, formatCents } from '../lib/format'
import { billAuthority, unavailabilitySentence } from '../lib/authority'
import type { Bill, ReferralCandidate, Scope } from '../lib/types'

type Panel = 'none' | 'pause' | 'resume' | 'refer'

/** What the last submission did, so the card can say so rather than just re-rendering. */
type Outcome = { readonly kind: 'idle' } | { readonly kind: 'done'; readonly sentence: string } | { readonly kind: 'failed'; readonly message: string }

export function BillPauseCard({ bill, scope, onChanged }: { bill: Bill; scope: Scope | null; onChanged: () => void }) {
  const [panel, setPanel] = useState<Panel>('none')
  const [comment, setComment] = useState('')
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' })
  const [submitting, setSubmitting] = useState(false)
  const [candidates, setCandidates] = useState<readonly ReferralCandidate[] | undefined>(undefined)
  const [candidatesConsidered, setCandidatesConsidered] = useState<number | undefined>(undefined)
  const [candidatesError, setCandidatesError] = useState<string | null>(null)
  const [chosen, setChosen] = useState<string>('')

  const authority = scope === null ? null : billAuthority(scope, bill)

  // ⚠ Nothing at all on a decided bill. `billAuthority` returns all three as
  // `null` for APPROVED/REJECTED, and rendering an empty card under the Decision
  // card would read as a surface that failed to load.
  if (authority === null || (authority.requestInfo === null && authority.resume === null && authority.refer === null)) {
    return null
  }

  const openPanel = (next: Panel) => {
    setPanel((current) => (current === next ? 'none' : next))
    setComment('')
    setOutcome({ kind: 'idle' })
    if (next === 'refer' && candidates === undefined && candidatesError === null) {
      // ⚠ Fetched on OPEN rather than on mount: this list is a per-bill
      // `authorise()` sweep over every active user, and running it for every bill
      // an officer opens — most of which they will never refer — is work nobody
      // asked for inside a 1 s p95 budget.
      api
        .referralCandidates(bill.reference)
        .then((response) => {
          setCandidates(response.candidates)
          setCandidatesConsidered(response.considered)
        })
        .catch((error: unknown) => {
          setCandidatesError(error instanceof Error ? error.message : 'the list of colleagues could not be loaded')
        })
    }
  }

  const submit = async (action: Panel) => {
    const text = comment.trim()
    if (text.length === 0) return
    setSubmitting(true)
    setOutcome({ kind: 'idle' })
    try {
      if (action === 'pause') {
        await api.requestBillInformation(bill.reference, text)
        setOutcome({
          kind: 'done',
          sentence:
            'paused. It has left its approval queue and no decision can be recorded against it until it is ' +
            'resumed. Your note is on the permanent ledger.',
        })
      } else if (action === 'resume') {
        const response = await api.resumeBill(bill.reference, text)
        // ⚠ The DESTINATION comes from the server's own answer, never from a guess
        // here. The client does not know which queue the ledger recorded, and
        // printing "back to level 1" because that is the common case would be
        // wrong for every bill that had already cleared level 1.
        setOutcome({
          kind: 'done',
          sentence: `resumed. It is now ${billStatusLabel(response.bill.status).toLowerCase()}.`,
        })
      } else if (action === 'refer') {
        if (chosen.length === 0) return
        const response = await api.referBill(bill.reference, chosen, text)
        // ⚠ The name the SERVER recorded, not the label that was clicked. Two
        // colleagues can share a name; echoing the server's answer makes this a
        // confirmation rather than a repetition of the request.
        setOutcome({
          kind: 'done',
          sentence: `referred to ${response.referredTo.displayName}. The bill has not moved: it is still ${billStatusLabel(
            response.bill.status,
          ).toLowerCase()}.`,
        })
      }
      setPanel('none')
      setComment('')
      setChosen('')
      onChanged()
    } catch (error: unknown) {
      setOutcome({ kind: 'failed', message: error instanceof Error ? error.message : 'the request failed' })
    } finally {
      setSubmitting(false)
    }
  }

  const refusals = [
    authority.requestInfo !== null && !authority.requestInfo.allowed
      ? { what: 'Pause for information', why: unavailabilitySentence(authority.requestInfo.why, 'REQUEST_INFO', formatCents) }
      : null,
    authority.resume !== null && !authority.resume.allowed
      ? { what: 'Resume', why: unavailabilitySentence(authority.resume.why, 'RESUME', formatCents) }
      : null,
    authority.refer !== null && !authority.refer.allowed
      ? { what: 'Refer to a colleague', why: unavailabilitySentence(authority.refer.why, 'REFER', formatCents) }
      : null,
  ].filter((r): r is { what: string; why: string } => r !== null)

  return (
    <div className="pt-card">
      <h2 className="text-lg mb-1">Pause, resume, or ask a colleague</h2>
      <p className="text-sm text-[var(--sgds-color-subtle)] mb-4 max-w-[70ch]">
        {bill.status === 'INFORMATION_REQUESTED'
          ? 'This bill is paused. It is waiting on the healthcare service provider, not on this office, and no ' +
            'approval or rejection can be recorded against it until it is resumed, which returns it to the exact ' +
            'queue it was paused out of.'
          : 'None of these decides the bill. Pausing takes it out of its approval queue until the provider supplies ' +
            'something; referring it leaves it exactly where it is and asks a colleague to look. Every one of them ' +
            'writes a permanent ledger entry against your identity, with your note.'}
      </p>

      {outcome.kind === 'failed' && (
        <div role="alert" className="pt-well p-3 mb-3 text-sm text-[var(--sgds-danger-color-emphasis)] pt-focus-clear">
          {plainProse(outcome.message)}
        </div>
      )}
      {outcome.kind === 'done' && (
        <div role="status" className="pt-well p-3 mb-3 text-sm pt-focus-clear">
          <span className="pt-mono font-semibold">{bill.reference}</span> {outcome.sentence}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-focus-clear">
        {authority.requestInfo?.allowed === true && (
          <SgdsButton variant="outline" type="button" onClick={() => openPanel('pause')}>
            {panel === 'pause' ? 'Cancel' : 'Request information'}
          </SgdsButton>
        )}
        {/* ★★★ THE RETURN PATH. A paused bill ALWAYS renders this, which is the
            whole reason `billAuthority` computes these three before its
            "nothing is pending" early return. */}
        {authority.resume?.allowed === true && (
          <SgdsButton variant="primary" type="button" onClick={() => openPanel('resume')}>
            {panel === 'resume' ? 'Cancel' : 'Resume this bill'}
          </SgdsButton>
        )}
        {authority.refer?.allowed === true && (
          <SgdsButton variant="outline" type="button" onClick={() => openPanel('refer')}>
            {panel === 'refer' ? 'Cancel' : 'Refer to a colleague'}
          </SgdsButton>
        )}
      </div>

      {panel !== 'none' && (
        <div className="pt-well p-4 mt-4 flex flex-col gap-3 pt-focus-clear">
          {panel === 'refer' && (
            <>
              {/*
                ★★★ A SELECT, NOT A TEXT FIELD. See this file's header: there is no
                name-resolution path in this product, deliberately.

                ⚠ Every option carries the ROLE beside the name, because two
                colleagues genuinely can share a display name and the role is the
                only other thing this list is allowed to publish (the approval level,
                the limit and the email are the authority structure, which is what
                the access-control matrix reports behind its own gate). The VALUE is
                always the id.
              */}
              {candidatesError !== null ? (
                <p role="alert" className="text-sm text-[var(--sgds-danger-color-emphasis)]">
                  The list of colleagues could not be loaded: {candidatesError}. Without it there is no way to name
                  a colleague: this form does not accept a typed name, because resolving one could file the
                  referral against the wrong person.
                </p>
              ) : candidates === undefined ? (
                <p className="text-sm text-[var(--sgds-body-color-subtle)]" aria-busy="true">
                  Finding the colleagues who can read this bill…
                </p>
              ) : candidates.length === 0 ? (
                <p className="text-sm">
                  <strong className="font-semibold">Nobody can be referred this bill.</strong>{' '}
                  <span className="text-[var(--sgds-body-color-subtle)]">
                    {candidatesConsidered ?? 0} other active user(s) were checked, and none holds a grant covering{' '}
                    {bill.agencyCode} {bill.billType} bills. That is a fact about grants rather than about
                    staffing: a referral to somebody who cannot open the bill would sit in a ledger nobody reads.
                  </span>
                </p>
              ) : (
                // ★ WAVE 26 — the house `SgdsSelect`, not a native `<select>`; the
                // hint rides its own `hintText` so it stays attached to the
                // control. Option VALUES are still the server's user ids and the
                // labels display names — no name is ever resolved to an id here.
                <SgdsSelect
                  label="Colleague"
                  value={chosen}
                  hintText={`${candidates.length} of ${candidatesConsidered ?? candidates.length} other active users can read this bill. Only they can be referred it.`}
                  onSgdsChange={(e) => setChosen(targetValue(e))}
                >
                  <SgdsSelectOption value="">Choose a colleague&hellip;</SgdsSelectOption>
                  {candidates.map((candidate) => (
                    <SgdsSelectOption key={candidate.userId} value={candidate.userId}>
                      {candidate.displayName}, {candidate.role}
                    </SgdsSelectOption>
                  ))}
                </SgdsSelect>
              )}
            </>
          )}

          <SgdsTextarea placeholder=""
            label={
              panel === 'pause'
                ? 'What must the provider supply?'
                : panel === 'resume'
                  ? 'What arrived?'
                  : 'What do you want your colleague to look at?'
            }
            hintText={
              panel === 'pause'
                ? 'Required. This is what the pause is for: a bill sent back with no question is one nobody can close.'
                : panel === 'resume'
                  ? 'Required. It explains the pause at the other end, for whoever reads this ledger next.'
                  : 'Required. Your colleague sees this on the bill’s own history; the bill does not move.'
            }
            rows={3}
            required
            value={comment}
            onSgdsInput={(e) => {
              const target = e.target as HTMLInputElement | null
              setComment(target?.value ?? '')
            }}
          />

          <div>
            <SgdsButton
              variant="primary"
              type="button"
              loading={submitting}
              disabled={comment.trim().length === 0 || (panel === 'refer' && chosen.length === 0)}
              onClick={() => void submit(panel)}
            >
              {panel === 'pause' ? 'Confirm pause' : panel === 'resume' ? 'Confirm resume' : 'Confirm referral'}
            </SgdsButton>
          </div>
        </div>
      )}

      {/*
        Every unavailable action gets a sentence, exactly as `DecisionCard` does —
        a control that is simply absent reads as a bug. And the sentences here are
        worth more than they look: the monetary ceiling does NOT bind these three,
        so an officer refused Approve on an expensive bill is still offered all
        three of these, and the asymmetry needs explaining rather than hiding.
      */}
      {refusals.length > 0 && (
        <div className="pt-well p-4 mt-4 flex flex-col gap-3 pt-focus-clear">
          {refusals.map((refusal) => (
            <p key={refusal.what} className="text-sm">
              <strong className="font-semibold">{refusal.what} is not available to you. </strong>
              <span className="text-[var(--sgds-body-color-subtle)]">{refusal.why}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
